import React, { useLayoutEffect, useRef, useState } from 'react'

// A bar pinned to the bottom of the viewport whose body is dragged open.
//
// The same gesture as the iOS app's: drag up to open, down to close, with a
// snap on release that a flick can win outright. It exists because a phone
// browser has the same problem the app did — a week grid several rows tall,
// pinned over the content it describes — and a bar that can only be tapped
// open makes you commit to the whole thing before you can see any of it.
//
// Pointer events rather than touch events, so a finger, a pen and a mouse all
// take one code path. `setPointerCapture` is what keeps a drag alive when the
// finger leaves the handle, which it always does — the handle is small and the
// gesture is long.
export default function BottomDrawer({ open, onOpenChange, summary, children }) {
  const contentRef = useRef(null)
  const [contentHeight, setContentHeight] = useState(0)
  // { startY, startHeight, height, moved } while a drag is in flight, else null.
  const [drag, setDrag] = useState(null)

  // Measured on the inner element, whose height is its own regardless of what
  // the clipping wrapper is currently showing. A ResizeObserver rather than a
  // one-off read because the week grid rewraps when the viewport changes, and
  // a stale height would clamp the drag short of the content.
  useLayoutEffect(() => {
    const element = contentRef.current
    if (!element) return undefined

    const measure = () => setContentHeight(element.offsetHeight)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const onPointerDown = (event) => {
    // Ignore secondary mouse buttons; a right-click is not a drag.
    if (event.button !== 0) return

    event.currentTarget.setPointerCapture(event.pointerId)
    setDrag({
      startY: event.clientY,
      startHeight: open ? contentHeight : 0,
      height: open ? contentHeight : 0,
      moved: false,
    })
  }

  const onPointerMove = (event) => {
    if (!drag) return

    // Dragging up is a negative delta, and up means open.
    const delta = event.clientY - drag.startY
    const height = Math.min(contentHeight, Math.max(0, drag.startHeight - delta))

    setDrag((current) => ({
      ...current,
      height,
      // A few pixels of slop, so a tap that wobbles is still a tap.
      moved: current.moved || Math.abs(delta) > 4,
    }))
  }

  const endDrag = () => {
    if (!drag) return

    // A gesture that never moved is a tap, and a tap toggles. Otherwise the
    // nearer end wins.
    const next = drag.moved ? drag.height > contentHeight / 2 : !open
    setDrag(null)
    onOpenChange(next)
  }

  const dragging = drag !== null
  const height = dragging ? drag.height : open ? contentHeight : 0

  return (
    <div className="sticky bottom-0 z-10 -mx-3 mt-4 border-t px-3 pt-1.5 backdrop-blur border-slate-800 bg-slate-950/95 pb-[max(0.875rem,calc(env(safe-area-inset-bottom)+0.5rem))] sm:-mx-4 sm:px-4">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={(event) => {
          // Keyboard users get the same control without the gesture.
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpenChange(!open)
          }
        }}
        // touch-none stops the browser claiming the vertical drag for page
        // scrolling before the handler sees it, which on a phone it otherwise
        // always does.
        className="w-full cursor-grab touch-none select-none active:cursor-grabbing"
      >
        <div className="mx-auto mb-1.5 h-1 w-9 rounded-full bg-slate-700" aria-hidden="true" />
        {summary}
      </div>

      <div
        className="overflow-hidden"
        style={{
          height,
          // No transition while the finger is down: the height is already
          // tracking it, and easing toward a moving target reads as lag.
          transition: dragging ? 'none' : 'height 180ms ease-out',
        }}
      >
        <div ref={contentRef} className="pt-2.5">
          {children}
        </div>
      </div>
    </div>
  )
}
